import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { RefreshCw, Home, Globe } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

interface ErrorPageProps {
    type: '404' | '500';
}

export const ErrorPage: React.FC<ErrorPageProps> = ({ type }) => {
    const { t, toggleLanguage, language } = useLanguage();

    const is404 = type === '404';
    const title = is404 ? t('error.404.title') : t('error.500.title');
    const subtitle = is404 ? t('error.404.subtitle') : t('error.500.subtitle');
    const message = is404 ? t('error.404.message') : t('error.500.message');

    return (
        <div className="min-h-screen flex flex-col items-center justify-center p-4 text-center relative overflow-hidden">
            <div className="absolute top-6 right-6 z-50">
                <button
                    onClick={toggleLanguage}
                    className="p-2 text-gray-400 hover:text-rs-bright transition-colors flex items-center gap-1 font-bold text-xs"
                    aria-label="Toggle Language"
                >
                    <Globe size={18} />
                    <span>{language === 'en' ? 'AR' : 'EN'}</span>
                </button>
            </div>

            <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-5 pointer-events-none fixed"></div>
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-rs-muted/10 rounded-full blur-[128px] pointer-events-none"></div>

            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="relative z-10"
            >
                <h1 className="text-9xl font-bold text-rs-bright mb-4 font-mono select-none drop-shadow-[0_0_15px_rgba(231,10,53,0.5)]">
                    {title}
                </h1>
                <h2 className="text-4xl font-bold text-white mb-6 drop-shadow-lg">{subtitle}</h2>
                <p className="text-gray-400 max-w-md mx-auto mb-10 text-lg leading-relaxed">
                    {message}
                </p>

                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                    {!is404 && (
                        <button
                            onClick={() => window.location.reload()}
                            className="inline-flex items-center justify-center gap-2 px-8 py-3.5 bg-rs-bright text-white rounded-lg transition-all duration-300 font-bold group shadow-[0_0_20px_rgba(231,10,53,0.3)] hover:shadow-[0_0_30px_rgba(231,10,53,0.6)] hover:scale-105"
                        >
                            <RefreshCw className="w-5 h-5 group-hover:rotate-180 transition-transform duration-500" />
                            <span>{t('error.tryAgain')}</span>
                        </button>
                    )}

                    <Link
                        to="/"
                        className={`inline-flex items-center justify-center gap-2 px-8 py-3.5 text-white rounded-lg transition-all duration-300 font-bold group ${!is404
                            ? 'bg-white/5 hover:bg-white/10 border border-white/10 hover:border-white/20 backdrop-blur-sm'
                            : 'bg-rs-bright shadow-[0_0_20px_rgba(231,10,53,0.3)] hover:shadow-[0_0_30px_rgba(231,10,53,0.6)] hover:scale-105'
                            }`}
                    >
                        <Home className="w-5 h-5 group-hover:scale-110 transition-transform" />
                        <span>{t('error.backHome')}</span>
                    </Link>
                </div>
            </motion.div>
        </div>
    );
};
